import { useNavigate } from "react-router-dom";
import Footer from "./Footer";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="font-poppins h-[calc(100vh-80px)] mt-[80px] overflow-y-auto scrollbar-hide">
      {/* Hero strip - same gradient as home */}
      <div
        className="w-full py-12 md:py-16 flex justify-center items-center"
        style={{
          background:
            "linear-gradient(90deg, #02537F 0%, #0585CB 60%, #035885 100%)",
        }}
      >
        <div className="mx-4 md:mx-6 lg:mx-14 text-center max-w-3xl">
          <h1 className="text-white font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-3">
            Privacy Policy
          </h1>
          <p className="text-white/90 text-base md:text-lg">
            Your privacy matters to us. Learn how we protect and use your information.
          </p>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="mx-4 md:mx-6 lg:mx-14 py-10 md:py-14">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-10">
          <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">Privacy Policy</h3>
              <p className="text-xs text-gray-500 mt-1">Last Updated: March 29, 2025</p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">1. Introduction</h4>
              <p>
                Sembe LLC dba Tydii ("Tydii," "we," "us," or "our") is committed to
                safeguarding your privacy. This Privacy Policy explains how we collect,
                use, disclose, and protect your personal information when you access our
                website, mobile application, and utilize any other aspect of our
                platform.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">2. What Information is Collected</h4>
              <p className="mb-3">We collect various types of information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <b>User Information:</b> When you register or utilize our platform, we collect personal data
                  including your name, email address, phone number, and driver's license information.
                </li>
                <li>
                  <b>Laundry or Order Information:</b> Details related to your laundry orders, such as pickup and
                  delivery addresses and order preferences will be collected when you place orders.
                </li>
                <li>
                  <b>Usage Data:</b> We gather information on your interactions within the platform, including
                  visited pages and actions taken.
                </li>
                <li>
                  <b>Operational Information:</b> Details relating to service providers' operations like sales
                  volume, customer volume, sales revenue, operational volume, service performance
                  metrics, service popularity, and productivity insights.
                </li>
                <li>
                  <b>Device Information:</b> Information about your device, such as IP address, browser type, and
                  operating system, may be collected
                </li>
              </ul>

              <p className="mt-3">
                The collection of this information is required unless specified
                otherwise.
              </p>

              <p className="mt-3">
                Tydii does not collect or hold any payment information. Payments made on our platform are
                processed by Stripe and all information you provide for payment is collected and held by Stripe
                in accordance with its own terms of service and privacy policy (
                <a
                  href="https://stripe.com/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#02537F] underline hover:text-[#035885]"
                >
                  https://stripe.com/privacy
                </a>
                ).
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">3. How We Collect Information</h4>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <b>From Uses of Our Services:</b> We receive information when you use our services, like
                  during account creation, order placement, communications with Tydii, and order
                  fulfillment.
                </li>
                <li>
                  <b>From Legal Claims, Disputes, Requests, and Orders:</b> We may collect personal
                  information from third parties, such as law enforcement agencies, government agencies
                  or authorities, users of our services, or any other party who may be involved in the
                  matter.
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">4. Use of Information</h4>
              <p className="mb-3">We utilize your information for the following purposes:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><b>Service Improvement:</b> To provide and enhance our platform.</li>
                <li><b>Order processing:</b> To process your orders and handle payments.</li>
                <li><b>Communication:</b> To correspond with you regarding your orders and our services.</li>
                <li><b>Customer Support:</b> To respond to inquiries and offer customer assistance.</li>
                <li><b>Marketing:</b> To send promotional offers and updates.</li>
                <li><b>Analytics:</b> To monitor and analyze usage patterns for service enhancement.</li>
                <li>Any legitimate business purpose.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">5. Sharing Your Information</h4>
              <p className="mb-3">We may share your personal information with third parties for specific purposes:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <b>Service Providers:</b> Third-party service providers assisting in delivering our services,
                  including payment processing, delivery, and customer support.
                </li>
                <li><b>Delivery Operators:</b> Third-party delivery operators (drivers) for delivery support</li>
                <li>
                  <b>Legal Compliance:</b> Disclosure as required by law, court order, or governmental
                  regulations.
                </li>
                <li>
                  <b>Business Transfers:</b> In the event of a sale, merger, acquisition, joint venture,
                  assignment, or restructuring of our business or assets, your information may be
                  transferred.
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">6. Data Security</h4>
              <p>
                We use commercially reasonable administrative, technical, and physical security controls to
                protect your personal information from unauthorized access. While we implement security
                measures to protect your information, we cannot guarantee absolute security due to the
                inherent risks of online data transmission.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">7. Children's Privacy</h4>
              <p>
                Our services are not directed at individuals under the age of 16. We do not knowingly collect
                personal information from children. If you believe that we have inadvertently collected
                information from a child, please contact us, and we will take appropriate measures to delete that
                information.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">8. User Choices</h4>
              <p className="mb-3">You have the following options regarding your information:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <b>Access and Correction:</b> Access, update, and correct your personal information through
                  your account settings.
                </li>
                <li><b>Unsubscribe:</b> Opt-out from receiving marketing communications at any time.</li>
                <li><b>Account Deletion:</b> Request the deletion of your account and associated data.</li>
                <li>Cancellation of auto-renewal of subscription services.</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">9. Changes to This Privacy Policy</h4>
              <p>
                We may update this Privacy Policy from time to time. When changes occur, we will revise the
                "Last Updated" date at the top of this policy and, if material changes are made, send an email
                notifying you that this Privacy Policy has changed to the email address associated with your
                account. We encourage you to review this policy periodically for updates.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-base text-gray-900 mb-2">10. Contact Information</h4>
              <p>
                For inquiries or concerns about this Privacy Policy, please contact us at:{" "}
                <a
                  href="mailto:help@tydii.io"
                  className="text-[#02537F] underline hover:text-[#035885]"
                >
                  help@tydii.io
                </a>
              </p>
            </div>

            <p className="font-medium text-gray-900 pt-4">
              Thank you for choosing Tydii.
            </p>
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="inline-flex items-center justify-center rounded-lg text-white bg-[#02537F] hover:bg-sky-800 px-6 py-2.5 text-base font-bold shadow-sm transition"
          >
            Back to Home
          </button>
        </div>
      </div>

      <div className="bg-[#034F78]">
        <Footer />
      </div>
    </div>
  );
}
